import React, { useState, ChangeEvent, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * This page allows a signed-in user to upload files to S3 using presigned data.
 * After upload completes or is canceled, it calls /send-upload-email to send a notification email.
 *
 * When canceled, the email will include:
 *   - The details (count, total size, file names) of the successfully uploaded files.
 *   - The count, total size, and names of the files that did not upload.
 */
export default function UploadPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(state?.user);

  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);

  const controllerRef = useRef<AbortController | null>(null);
  // Remember the originally selected files.
  const selectedFilesRef = useRef<File[]>([]);
  // Keep track of successfully uploaded files.
  const uploadedFilesRef = useRef<File[]>([]);
  // A flag indicating if cancel has been triggered.
  const cancelTriggeredRef = useRef<boolean>(false);
  // Track files flagged for pyramidal conversion that were successfully uploaded.
  const npFlaggedFilesRef = useRef<string[]>([]);

  // Use the API URL from your environment
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const FIVE_MIN = 5 * 60;

  if (!user) {
    return (
      <div className="p-6 text-red-500">
        No user info found. Please sign in again.
      </div>
    );
  }

  /**
   * Utility function to format bytes to an appropriate unit.
   * (Very similar to your front-end formatBytes, but re-usable.)
   */
  const formatBytesDynamic = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let val = bytes;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i++;
    }
    return `${val.toFixed(2)} ${units[i]}`;
  };

  /**
   * Suffix appended to non-pyramidal TIFF filenames before upload.
   * The Lambda function in AWS looks for this suffix to trigger conversion.
   */
  const NP_SUFFIX = '-__NP__';

  /**
   * File extensions that support pyramidal/tiled format.
   * These are checked for pyramid structure before upload.
   */
  const PYRAMID_EXTENSIONS = /\.(tiff?)$/i;

  /**
   * Check if a TIFF file is pyramidal/tiled by reading its header.
   * Reads the first ~16KB and parses IFD entries looking for:
   *   - Multiple IFDs (multiple resolution levels)
   *   - TileWidth (tag 322) and TileLength (tag 323)
   * Returns true if the TIFF appears to be pyramidal/tiled.
   */
  const checkTiffIsPyramidal = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const view = new DataView(buffer);

          const byteOrder = view.getUint16(0, false);
          const le = byteOrder === 0x4949;
          const magic = view.getUint16(2, le);
          const isBigTiff = magic === 43;

          let ifdOffset: number;
          if (isBigTiff) {
            const lo = view.getUint32(8, le);
            const hi = view.getUint32(12, le);
            ifdOffset = le ? lo + hi * 0x100000000 : hi + lo * 0x100000000;
          } else {
            ifdOffset = view.getUint32(4, le);
          }

          let ifdCount = 0;
          let hasTileWidth = false;
          let hasTileLength = false;
          let offset = ifdOffset;

          while (offset > 0 && offset < buffer.byteLength - 2 && ifdCount < 10) {
            ifdCount++;
            let numEntries: number;
            let entrySize: number;

            if (isBigTiff) {
              if (offset + 8 > buffer.byteLength) break;
              numEntries = view.getUint32(offset, le);
              entrySize = 20;
              offset += 8;
            } else {
              if (offset + 2 > buffer.byteLength) break;
              numEntries = view.getUint16(offset, le);
              entrySize = 12;
              offset += 2;
            }

            for (let i = 0; i < numEntries; i++) {
              const entryOffset = offset + i * entrySize;
              if (entryOffset + 2 > buffer.byteLength) break;
              const tag = view.getUint16(entryOffset, le);
              if (tag === 322) hasTileWidth = true;
              if (tag === 323) hasTileLength = true;
            }

            const nextOffset = offset + numEntries * entrySize;
            if (isBigTiff) {
              if (nextOffset + 8 > buffer.byteLength) break;
              const lo = view.getUint32(nextOffset, le);
              const hi = view.getUint32(nextOffset + 4, le);
              offset = le ? lo + hi * 0x100000000 : hi + lo * 0x100000000;
            } else {
              if (nextOffset + 4 > buffer.byteLength) break;
              offset = view.getUint32(nextOffset, le);
            }
          }

          const isPyramidal = ifdCount > 1 && hasTileWidth && hasTileLength;
          resolve(isPyramidal);
        } catch {
          resolve(true);
        }
      };
      reader.onerror = () => resolve(true);
      reader.readAsArrayBuffer(file.slice(0, 16384));
    });
  };

  /**
   * Inserts NP suffix before the file extension.
   * e.g. "slide.tif" -> "slide-__NP__.tif"
   */
  const addNpSuffix = (filename: string): string => {
    const dotIndex = filename.lastIndexOf('.');
    if (dotIndex === -1) return filename + NP_SUFFIX;
    return filename.substring(0, dotIndex) + NP_SUFFIX + filename.substring(dotIndex);
  };

  /**
   * Strips the NP suffix from a filename for display purposes.
   * e.g. "slide-__NP__.tif" -> "slide.tif"
   */
  const stripNpSuffix = (filename: string): string => {
    return filename.replace(NP_SUFFIX, '');
  };

  /* ─── renew presigned-POST if <5 min remain ─────────────────────────── */
  const refreshIfExpiring = async () => {
    if (!user?.expiresAt) return;                       // no expiry info (old user)
    const now = Math.floor(Date.now() / 1000);
    if (user.expiresAt - now > FIVE_MIN) return;        // still fresh

    const res = await fetch(`${API_URL}/renew-presigned`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ username: user.username })
    });
    if (!res.ok) throw new Error('Failed to renew upload credentials');
    const data = await res.json();
    setUser(data.user);                                 // swap in fresh fields
  };  

  const handleUpload = async () => {
    if (files.length === 0) {
      setStatus(['Please select one or more files to upload.']);
      return;
    }

    // Reset flags and refs.
    cancelTriggeredRef.current = false;
    selectedFilesRef.current = files;
    uploadedFilesRef.current = [];
    npFlaggedFilesRef.current = [];

    setUploading(true);
    setProgress(1);
    const newStatus: string[] = [];
    let uploadedSize = 0;

    try {
      for (const file of files) {
        await refreshIfExpiring();

        // Check if this is a non-pyramidal TIFF/SVS/NDPI
        let uploadFilename = file.name.replace(/-__NP__/g, '');
        if (PYRAMID_EXTENSIONS.test(uploadFilename)) {
          const isPyramidal = await checkTiffIsPyramidal(file);
          if (!isPyramidal) {
            uploadFilename = addNpSuffix(file.name);
          }
        }

        const key = user.key.replace('${filename}', uploadFilename);
        const formData = new FormData();
        formData.append('key', key);
        formData.append('x-amz-algorithm', user.xAmzAlgorithm);
        formData.append('x-amz-credential', user.xAmzCredential);
        formData.append('x-amz-date', user.xAmzDate);
        formData.append('x-amz-signature', user.xAmzSignature);
        formData.append('policy', user.policy);
        formData.append('file', file);

        controllerRef.current = new AbortController();
        const res = await fetch(user.uploadUrl, {
          method: 'POST',
          body: formData,
          signal: controllerRef.current.signal,
        });

        if (res.ok) {
          const isNpFlagged = uploadFilename !== file.name;
          const npNote = isNpFlagged ? ' (queued for pyramidal conversion)' : '';
          newStatus.push(`✅ Uploaded ${file.name}${npNote}`);
          uploadedFilesRef.current.push(file);
          if (isNpFlagged) npFlaggedFilesRef.current.push(file.name);
          uploadedSize += file.size;
        } else {
          newStatus.push(`❌ Upload failed for ${file.name}`);
        }

        // Update the progress bar based on total size.
        setProgress(Math.round((uploadedSize / totalSize) * 100));
      }
      setStatus(newStatus);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        newStatus.push('⛔ Upload cancelled.');
      } else {
        console.error('Upload error:', err);
        newStatus.push(`❌ Error during upload: ${err.message}`);
      }
      setStatus(newStatus);
    } finally {
      // Only send a completion email if cancel was NOT triggered.
      if (!cancelTriggeredRef.current) {
        await sendReceiptEmail(false, uploadedFilesRef.current, [], npFlaggedFilesRef.current);
      }
      setUploading(false);
      setFiles([]);
      setTotalSize(0);
      setProgress(0);
    }
  };

  const handleCancelUpload = async () => {
    cancelTriggeredRef.current = true;
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    setUploading(false);

    // Compute the files that were not uploaded.
    const uploadedNames = uploadedFilesRef.current.map((file) => file.name);
    const notUploadedFiles = selectedFilesRef.current.filter(
      (file) => !uploadedNames.includes(file.name)
    );

    // Send one consolidated cancellation email with both sections.
    await sendReceiptEmail(true, uploadedFilesRef.current, notUploadedFiles, npFlaggedFilesRef.current);
  };

  /**
   * Sends a receipt email with details of uploaded files.
   * For cancelled uploads, it includes additional info for files not uploaded.
   *
   * @param canceled - true if this is a cancellation notification.
   * @param uploadedFiles - array of successfully uploaded files.
   * @param notUploadedFiles - array of files that were not uploaded.
   */
  const sendReceiptEmail = async (
    canceled: boolean,
    uploadedFiles: File[],
    notUploadedFiles: File[],
    npFlaggedFiles: string[] = []
  ) => {
    try {
      const fileNames = uploadedFiles.map((file) => stripNpSuffix(file.name));
      const uploadedTotalSize = uploadedFiles.reduce((acc, file) => acc + file.size, 0);
      const notUploadedNames = notUploadedFiles.map((file) => file.name);
      const notUploadedCount = notUploadedFiles.length;
      const notUploadedTotalSize = notUploadedFiles.reduce((acc, file) => acc + file.size, 0);

      const bodyData = {
        username: user.username,
        date: new Date().toISOString(),
        totalSize: uploadedTotalSize,
        fileCount: uploadedFiles.length,
        fileNames,
        canceled,
        key: user.key,
        // Extra fields for cancellations:
        notUploadedFileNames: notUploadedNames,
        notUploadedFileCount: notUploadedCount,
        notUploadedTotalSize: notUploadedTotalSize,
        // Files queued for pyramidal conversion:
        npFlaggedFileNames: npFlaggedFiles,
      };

      await fetch(`${API_URL}/send-upload-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });
    } catch (error) {
      console.error('Failed to send receipt email:', error);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
      const sizeSum = selectedFiles.reduce((acc, file) => acc + file.size, 0);
      setTotalSize(sizeSum);
      setProgress(0);
      setStatus([]);
    }
  };

  const handleSignOut = () => {
    navigate('/');
  };

  /**
   * Original simpler format function.
   * If you prefer the dynamic approach, you can replace calls with formatBytesDynamic.
   */
  const formatBytes = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="max-w-xl w-full bg-white p-6 rounded-xl shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Welcome, {user.username}!</h2>
          <button onClick={handleSignOut} className="text-sm text-blue-600 hover:underline">
            Sign out
          </button>
        </div>

        <input
          type="file"
          multiple
          onChange={handleFileChange}
          className="mb-4 w-full"
          disabled={uploading}
        />

        {files.length > 0 && (
          <p className="mb-2 text-sm text-gray-600">
            Selected {files.length} file{files.length > 1 ? 's' : ''} totaling {formatBytes(totalSize)}
          </p>
        )}

        {progress > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div className="bg-blue-600 h-3 rounded-full" style={{ width: `${progress}%` }}></div>
          </div>
        )}

        <button
          onClick={handleUpload}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 w-full"
          disabled={uploading}
        >
          Upload Files
        </button>

        {uploading && (
          <button onClick={handleCancelUpload} className="mt-2 text-sm text-red-600 hover:underline">
            Cancel Upload
          </button>
        )}

        {status.length > 0 && (
          <div className="mt-4 space-y-1 text-sm text-gray-700">
            {status.map((msg, idx) => (
              <p key={idx}>{msg}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
